<template>
  <div class="max-w-full mx-auto px-6 py-6 dark:bg-gray-600 dark:border-gray-600">
    <!-- Top bar: status pill + keyword + filter button -->
    <div class="flex justify-between items-center mb-4">
      <div class="flex space-x-4 items-center">
        <select v-model="filters.status" @change="refreshActiveTab"
          class="mt-1 block rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
          <option value="All">All</option>
          <option value="Open">Open</option>
          <option value="Closed">Closed</option>
        </select>
        <input v-model="filters.keywords" type="search" placeholder="Search words (min 3 chars) or a ticket / job #…"
          class="mt-1 block w-80 rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
          @keydown.enter="refreshActiveTab" />
      </div>
      <div class="flex items-center space-x-2" @click="showFilters = !showFilters">
        <button class="bg-yellow-500 text-white px-4 py-2 rounded-md flex items-center font-medium hover:bg-yellow-400">
          <svg class="h-6 w-6 text-white" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
            stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" />
            <path d="M5.5 5h13a1 1 0 0 1 0.5 1.5L14 12L14 19L10 16L10 12L5 6.5a1 1 0 0 1 0.5 -1.5" />
          </svg>
          Filter
        </button>
        <button class="focus:outline-none transition-transform duration-200">
          <svg :class="{ 'rotate-180': showFilters }"
            class="w-4 h-4 text-gray-600 dark:text-white transition-transform duration-200" aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 10">
            <path
              d="M15.434 1.235A2 2 0 0 0 13.586 0H2.414A2 2 0 0 0 1 3.414L6.586 9a2 2 0 0 0 2.828 0L15 3.414a2 2 0 0 0 .434-2.179Z" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Filter sidebar -->
    <FilterSidebar :visible="showFilters" :userroles="[]" @close="showFilters = false" @apply="onApplyFilters"
      @clear="onClearFilters">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Company -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Company</label>
          <select v-model="filters.company_id"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
            <option value="">All</option>
            <option v-for="c in lookups.companies" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>

        <!-- Tech grouped -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Tech</label>
          <select v-model="filters.tech_id"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
            <option value="">All</option>
            <template v-for="(techList, deptName) in groupedTechnicians" :key="deptName">
              <optgroup v-if="deptName" :label="deptName">
                <option v-for="tech in techList" :key="tech.id" :value="tech.id">{{ tech.name }}</option>
              </optgroup>
              <template v-else>
                <option v-for="tech in techList" :key="tech.id" :value="tech.id">{{ tech.name }}</option>
              </template>
            </template>
          </select>
        </div>

        <!-- Requester typeahead (client-side) -->
        <div v-click-outside="closeReqDropdown">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Requester</label>
          <div class="relative">
            <input type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
              placeholder="Requester Name" v-model="requesterName" @focus="focusReq" @blur="emptyReq"
              @keydown.down.prevent="onKeyDown('requester', 'down')" @keydown.up.prevent="onKeyDown('requester', 'up')"
              @keydown.enter.prevent="onEnter('requester', selectRequester)" />
            <button v-if="requesterName && requesterName !== 'All'" @click="clearRequester"
              class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
                <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
          </div>
          <ul v-if="dropdownState.requester.visible" data-field="requester"
            class="absolute z-10 bg-white mt-1 rounded-md shadow-lg max-h-64 overflow-y-auto dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
            <li v-for="(req, index) in filteredRequesters" :key="req.id" v-show="req.name && req.name.trim() !== ''"
              @mousedown.prevent="selectRequester(req)" :class="[
                'cursor-pointer p-2 text-sm',
                dropdownState.requester.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              ]">
              {{ req.name }}
              <span v-if="req.email" class="text-xs text-gray-500 dark:text-gray-200">&nbsp;&mdash; {{ req.email
              }}</span>
            </li>
          </ul>
        </div>

        <!-- Ticket type typeahead (client-side) -->
        <div v-click-outside="closeTypeDropdown">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Ticket Type</label>
          <div class="relative">
            <input type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
              placeholder="Ticket Type" v-model="ticketTypeName" @focus="focusType" @blur="emptyType"
              @keydown.down.prevent="onKeyDown('type', 'down')" @keydown.up.prevent="onKeyDown('type', 'up')"
              @keydown.enter.prevent="onEnter('type', selectType)" />
            <button v-if="ticketTypeName && ticketTypeName !== 'All'" @click="clearType"
              class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
                <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
          </div>
          <ul v-if="dropdownState.type.visible" data-field="type"
            class="absolute z-10 bg-white mt-1 rounded-md shadow-lg max-h-64 overflow-y-auto dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
            <li v-for="(tt, index) in filteredTypes" :key="tt.id" @mousedown.prevent="selectType(tt)" :class="[
              'cursor-pointer p-2 text-sm',
              dropdownState.type.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            ]">{{ tt.name }}</li>
          </ul>
        </div>

        <!-- Date range -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">From</label>
          <input v-model="filters.start_date" type="date"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">To</label>
          <input v-model="filters.end_date" type="date"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100" />
        </div>
      </div>
    </FilterSidebar>

    <!-- Applied filter bubbles -->
    <div v-if="appliedFilterBubbles.length" class="flex flex-wrap gap-2 mb-4 items-center">
      <div class="text-gray-800 dark:text-gray-100 font-semibold">Filters Applied:</div>
      <span v-for="b in appliedFilterBubbles" :key="b.key"
        class="bg-yellow-500 text-white text-sm font-medium px-3 py-1 rounded-full flex items-center">
        {{ b.label }}
        <button @click="clearSpecificFilter(b.key)" class="ml-2 text-white hover:text-red-200">&times;</button>
      </span>
    </div>
    <div v-else class="text-gray-500 dark:text-gray-300 font-semibold mb-4">Filters Applied: None</div>

    <!-- Tabs -->
    <div class="border-b border-gray-200 dark:border-gray-500 mb-4">
      <nav class="-mb-px flex space-x-8">
        <button v-for="t in tabs" :key="t.key" @click="switchTab(t.key)" :class="[
          'whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium focus:outline-none',
          activeTab === t.key
            ? 'border-yellow-500 text-gray-900 dark:text-gray-100'
            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-300'
        ]">{{ t.label }}</button>
      </nav>
    </div>

    <div class="mb-2 flex items-center justify-between text-sm text-gray-700 dark:text-gray-100">
      <span v-if="countLabel">{{ countLabel }}</span>
      <span v-if="activeSort" class="text-xs text-gray-500 dark:text-gray-300">sorted by {{ activeSort }}</span>
    </div>


    <div v-if="activeTab === 'all'">
      <section class="mb-6">
        <h3 class="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
          Tickets
          <span class="text-xs font-normal text-gray-500 dark:text-gray-300">
            ({{ formatTotal(all.tickets.total, all.tickets.totalIsApproximate) }})
          </span>
          <button v-if="all.tickets.total > all.tickets.data.length" @click="switchTab('tickets')"
            class="ml-3 text-xs text-blue-600 dark:text-blue-300 hover:underline">View all &rarr;</button>
        </h3>
        <TicketsTable :rows="all.tickets.data" :sortOrder="all.tickets.order" @open="openTicket"
          @openTicketV2="openTicketV2" @sort="onSortAllTickets" />
      </section>
      <section>
        <h3 class="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
          Jobs
          <span class="text-xs font-normal text-gray-500 dark:text-gray-300">
            ({{ formatTotal(all.jobs.total, all.jobs.totalIsApproximate) }})
          </span>
          <button v-if="all.jobs.total > all.jobs.data.length" @click="switchTab('jobs')"
            class="ml-3 text-xs text-blue-600 dark:text-blue-300 hover:underline">View all &rarr;</button>
        </h3>
        <JobsTable :rows="all.jobs.data" :sortOrder="all.jobs.order" @open="openJob" @openTicketV2="openTicketV2"
          @sort="onSortAllJobs" />
      </section>
    </div>

    <div v-else-if="activeTab === 'tickets'">
      <TicketsTable :rows="tickets.data" :sortOrder="tickets.order" @open="openTicket" @openTicketV2="openTicketV2"
        @sort="onSortTickets" />
      <Pagination :page="tickets.page" :totalPages="ticketsTotalPages" @change="(p) => fetchTickets(p)" />
    </div>

    <div v-else-if="activeTab === 'jobs'">
      <JobsTable :rows="jobs.data" :sortOrder="jobs.order" @open="openJob" @openTicketV2="openTicketV2"
        @sort="onSortJobs" />
      <Pagination :page="jobs.page" :totalPages="jobsTotalPages" @change="(p) => fetchJobs(p)" />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick, watch, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { searchRequesters } from '@/utils/requesterSearch';
import FilterSidebar from '@/views/components/FilterSidebar.vue';
import TicketsTable from './TicketsTable.vue';
import JobsTable from './JobsTable.vue';
import Pagination from './Pagination.vue';

const STORAGE_KEY = 'reporting.filters';
const router = useRouter();
const { proxy } = getCurrentInstance();
const updateLoading = (n) => proxy?.$updateLoading?.(n);

const tabs = [
  { key: 'all', label: 'All' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'jobs', label: 'Jobs' },
];

const defaults = () => ({
  keywords: '',
  status: 'All',
  company_id: '',
  tech_id: '',
  requester_id: '',
  ticket_type_id: '',
  start_date: '',
  end_date: '',
});

const filters = reactive(loadFilters());

// Display-only text for typeahead inputs (decoupled from filter IDs)
const requesterName = ref('');
const ticketTypeName = ref('');

const activeTab = ref('all');
const showFilters = ref(false);
const loading = ref(false);

// Shared lookups
const lookups = ref({ companies: [], ticket_types: [] });
const requesters = ref([]);
const allTech = ref([]);
const departments = ref([]);

const dropdownState = reactive({
  requester: { visible: false, items: [], index: -1 },
  type: { visible: false, items: [], index: -1 },
});

const all = reactive({
  tickets: { data: [], total: 0, totalIsApproximate: false, sort: '', order: 'DESC' },
  jobs: { data: [], total: 0, totalIsApproximate: false, sort: '', order: 'DESC' },
});
const tickets = reactive({ data: [], total: 0, totalIsApproximate: false, page: 1, perPage: 25, sort: '', order: 'DESC' });
const jobs = reactive({ data: [], total: 0, totalIsApproximate: false, page: 1, perPage: 25, sort: '', order: 'DESC' });

const ticketsTotalPages = computed(() => Math.max(1, Math.ceil((tickets.total || 0) / tickets.perPage)));
const jobsTotalPages = computed(() => Math.max(1, Math.ceil((jobs.total || 0) / jobs.perPage)));

const countLabel = computed(() => {
  if (activeTab.value === 'all') return '';
  const src = activeTab.value === 'tickets' ? tickets : jobs;
  if (src.total === 0 && !src.data.length) return '';
  return `${formatTotal(src.total, src.totalIsApproximate)} result${src.total === 1 ? '' : 's'}`;
});
const activeSort = computed(() => {
  if (activeTab.value === 'all') return '';
  const src = activeTab.value === 'tickets' ? tickets : jobs;
  return src.sort || '';
});

// ---- Tech grouping (mirrors TicketsSummary) ----
const groupedTechnicians = computed(() => {
  const groups = {};
  const ungrouped = [];
  (allTech.value || []).forEach(tech => {
    if (!tech.deptId) {
      ungrouped.push({ id: tech.id, name: tech.name });
      return;
    }
    let key;
    if (tech.deptId === 'External Tech') key = 'External Tech';
    else if (tech.details) key = tech.details;
    else key = 'Other Department';
    if (!groups[key]) groups[key] = [];
    let displayName = tech.name;
    if (tech.deptId === 'External Tech' && tech.details) displayName += ` (${tech.details})`;
    groups[key].push({ id: tech.id, name: displayName });
  });
  if (ungrouped.length) groups[''] = ungrouped;
  const keys = Object.keys(groups).filter(k => k !== 'External Tech').sort((a, b) => {
    if (a === '') return -1;
    if (b === '') return 1;
    return a.localeCompare(b);
  });
  if (groups['External Tech']) keys.push('External Tech');
  const ordered = {};
  keys.forEach(k => { ordered[k] = groups[k]; });
  return ordered;
});

// ---- Typeahead filtering ----
const filteredRequesters = computed(() => {
  const q = (requesterName.value || '').trim().toLowerCase();
  const out = q
    // The selected company's own people are listed first.
    ? searchRequesters(requesters.value, q, filters.company_id, { limit: 50 })
    : (requesters.value || []).slice(0, 50);
  dropdownState.requester.items = out;
  return out;
});
const filteredTypes = computed(() => {
  const q = (ticketTypeName.value || '').trim().toLowerCase();
  const out = q
    ? (lookups.value.ticket_types || []).filter(t => (t.name || '').toLowerCase().includes(q))
    : (lookups.value.ticket_types || []);
  dropdownState.type.items = out;
  return out;
});

// ---- Applied filter bubbles ----
// Resolve IDs to names via lookup arrays, falling back to the typeahead text refs
// so bubbles never show a raw ID even before lookups finish loading.
const companyName = computed(() => {
  const v = filters.company_id;
  if (!v) return '';
  const c = (lookups.value.companies || []).find(x => String(x.id) === String(v));
  return c ? c.name : '';
});
const techName = computed(() => {
  const v = filters.tech_id;
  if (!v) return '';
  const t = (allTech.value || []).find(x => String(x.id) === String(v));
  return t ? t.name : '';
});

const appliedFilterBubbles = computed(() => {
  const rows = [
    { key: 'keywords', prefix: 'Search', value: filters.keywords },
    { key: 'status', prefix: 'Status', value: filters.status === 'All' ? '' : filters.status },
    { key: 'company_id', prefix: 'Company', value: companyName.value },
    { key: 'tech_id', prefix: 'Tech', value: techName.value },
    { key: 'requester_id', prefix: 'Requester', value: requesterName.value },
    { key: 'ticket_type_id', prefix: 'Type', value: ticketTypeName.value },
    { key: 'start_date', prefix: 'From', value: filters.start_date },
    { key: 'end_date', prefix: 'To', value: filters.end_date },
  ];
  return rows
    .filter(r => r.value && String(r.value).trim() !== '' && r.value !== 'All')
    .map(r => ({ key: r.key, label: `${r.prefix}: ${r.value}` }));
});

function clearSpecificFilter(key) {
  if (!(key in filters)) return;
  if (key === 'status') filters.status = 'All';
  else filters[key] = '';
  if (key === 'requester_id') requesterName.value = '';
  if (key === 'ticket_type_id') ticketTypeName.value = '';
  refreshActiveTab();
}

function formatTotal(n, approx) {
  const v = Number(n || 0).toLocaleString();
  return approx ? `${v}+` : v;
}

function loadFilters() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return saved ? { ...defaults(), ...saved } : defaults();
  } catch { return defaults(); }
}
watch(filters, (v) => localStorage.setItem(STORAGE_KEY, JSON.stringify(v)), { deep: true });

function cleanFilters(f) {
  const out = {};
  for (const [k, v] of Object.entries(f)) {
    if (v !== '' && v !== null && v !== undefined && v !== 'All') out[k] = v;
  }
  return out;
}

// ---- API ----
async function loadLookups() {
  try {
    const [lu, req, techs, depts] = await Promise.all([
      axios.get('reporting/filters'),
      axios.get('requesters/').catch(() => ({ data: [] })),
      axios.get('user/techs').catch(() => ({ data: [] })),
      axios.get('departments/').catch(() => ({ data: [] })),
    ]);
    lookups.value = { companies: lu.data?.companies || [], ticket_types: lu.data?.ticket_types || [] };
    requesters.value = (req.data || []).filter(r => r && r.name && r.name.trim() !== '');
    allTech.value = techs.data || [];
    departments.value = depts.data || [];

    // Hydrate display strings for saved filter IDs
    if (filters.requester_id) {
      const r = requesters.value.find(x => x.id == filters.requester_id);
      if (r) requesterName.value = r.name;
    }
    if (filters.ticket_type_id) {
      const t = lookups.value.ticket_types.find(x => x.id == filters.ticket_type_id);
      if (t) ticketTypeName.value = t.name;
    }
  } catch (e) { console.error(e); }
}

async function fetchAll() {
  loading.value = true;
  updateLoading(1);
  // Reset All-tab sort orders to default since searchAll always returns DESC
  all.tickets.order = 'DESC';
  all.jobs.order = 'DESC';
  try {
    const { data } = await axios.post('reporting/search', { ...cleanFilters(filters), perPage: 10 });
    Object.assign(all.tickets, { data: data.tickets?.data || [], total: data.tickets?.total || 0, totalIsApproximate: !!data.tickets?.totalIsApproximate, sort: data.tickets?.sort || '' });
    Object.assign(all.jobs, { data: data.jobs?.data || [], total: data.jobs?.total || 0, totalIsApproximate: !!data.jobs?.totalIsApproximate, sort: data.jobs?.sort || '' });
  } catch (e) { console.error(e); }
  finally { loading.value = false; updateLoading(-1); }
}
async function fetchAllTicketsSection() {
  loading.value = true;
  updateLoading(1);
  try {
    const { data } = await axios.post('reporting/tickets/search', { ...cleanFilters(filters), page: 1, perPage: 10, sort: 'submitted', order: all.tickets.order });
    Object.assign(all.tickets, { data: data.data || [], total: data.total || 0, totalIsApproximate: !!data.totalIsApproximate, sort: data.sort || '' });
  } catch (e) { console.error(e); }
  finally { loading.value = false; updateLoading(-1); }
}
async function fetchAllJobsSection() {
  loading.value = true;
  updateLoading(1);
  try {
    const { data } = await axios.post('reporting/jobs/search', { ...cleanFilters(filters), page: 1, perPage: 10, sort: 'jdate', order: all.jobs.order });
    Object.assign(all.jobs, { data: data.data || [], total: data.total || 0, totalIsApproximate: !!data.totalIsApproximate, sort: data.sort || '' });
  } catch (e) { console.error(e); }
  finally { loading.value = false; updateLoading(-1); }
}
async function fetchTickets(page = 1) {
  loading.value = true;
  updateLoading(1);
  try {
    const { data } = await axios.post('reporting/tickets/search', { ...cleanFilters(filters), page, perPage: tickets.perPage, sort: 'submitted', order: tickets.order });
    Object.assign(tickets, { data: data.data || [], total: data.total || 0, totalIsApproximate: !!data.totalIsApproximate, page, sort: data.sort || '' });
  } catch (e) { console.error(e); }
  finally { loading.value = false; updateLoading(-1); }
}
async function fetchJobs(page = 1) {
  loading.value = true;
  updateLoading(1);
  try {
    const { data } = await axios.post('reporting/jobs/search', { ...cleanFilters(filters), page, perPage: jobs.perPage, sort: 'jdate', order: jobs.order });
    Object.assign(jobs, { data: data.data || [], total: data.total || 0, totalIsApproximate: !!data.totalIsApproximate, page, sort: data.sort || '' });
  } catch (e) { console.error(e); }
  finally { loading.value = false; updateLoading(-1); }
}

function toggleOrder(o) { return o === 'ASC' ? 'DESC' : 'ASC'; }
function onSortTickets() { tickets.order = toggleOrder(tickets.order); fetchTickets(1); }
function onSortJobs() { jobs.order = toggleOrder(jobs.order); fetchJobs(1); }
function onSortAllTickets() { all.tickets.order = toggleOrder(all.tickets.order); fetchAllTicketsSection(); }
function onSortAllJobs() { all.jobs.order = toggleOrder(all.jobs.order); fetchAllJobsSection(); }

function refreshActiveTab() {
  if (activeTab.value === 'all') return fetchAll();
  if (activeTab.value === 'tickets') return fetchTickets(1);
  if (activeTab.value === 'jobs') return fetchJobs(1);
}
function switchTab(k) {
  activeTab.value = k;
  refreshActiveTab();
}
function onApplyFilters() { showFilters.value = false; refreshActiveTab(); }
function onClearFilters() {
  Object.assign(filters, defaults());
  requesterName.value = '';
  ticketTypeName.value = '';
  refreshActiveTab();
}

// ---- Row navigation ----
function openTicket(row) { router.push({ name: 'reportingTicketDetail', params: { id: row.id } }); }
function openJob(row) { router.push({ name: 'reportingJobDetail', params: { id: row.id } }); }
function openTicketV2(id) { if (id) router.push({ path: `/ticket/${id}` }); }

// ---- Typeahead keyboard / open / clear ----
function onKeyDown(field, dir) {
  const s = dropdownState[field];
  if (!s.visible || !s.items.length) return;
  const n = s.items.length;
  s.index = dir === 'down' ? (s.index + 1) % n : (s.index - 1 + n) % n;
  nextTick(() => {
    const container = document.querySelector(`.scrollable-requester-list[data-field="${field}"]`);
    container?.querySelector('.active-item')?.scrollIntoView({ block: 'nearest' });
  });
}
function onEnter(field, selectFn) {
  const s = dropdownState[field];
  if (s.index >= 0 && s.index < s.items.length) {
    selectFn(s.items[s.index]);
    s.index = -1;
  }
}

function focusReq() { dropdownState.requester.visible = true; }
function emptyReq() { setTimeout(() => { dropdownState.requester.visible = false; }, 200); }
function closeReqDropdown() { dropdownState.requester.visible = false; }
function selectRequester(r) {
  filters.requester_id = r.id;
  requesterName.value = r.name;
  dropdownState.requester.visible = false;
  refreshActiveTab();
}
function clearRequester() {
  filters.requester_id = '';
  requesterName.value = '';
  refreshActiveTab();
}

function focusType() { dropdownState.type.visible = true; }
function emptyType() { setTimeout(() => { dropdownState.type.visible = false; }, 200); }
function closeTypeDropdown() { dropdownState.type.visible = false; }
function selectType(t) {
  filters.ticket_type_id = t.id;
  ticketTypeName.value = t.name;
  dropdownState.type.visible = false;
  refreshActiveTab();
}
function clearType() {
  filters.ticket_type_id = '';
  ticketTypeName.value = '';
  refreshActiveTab();
}

onMounted(async () => {
  await Promise.all([
    loadLookups(),
    refreshActiveTab(),
  ]);
});
</script>
